import { AxiosError } from 'axios';
import { cleanAxiosError, isAxiosResponseAsError, toAxiosLogFields } from '@honeybook/utils';
import { isProduction } from '@honeybook/utils-web';
import { logToConsole } from './logger.utils';
function debounceReportRecurringDataDogErrors() {
    const recurringErrors = new Map();
    return (error, context) => {
        let errorMessage;
        if (error) {
            if (typeof error === 'string') {
                errorMessage = error;
            }
            else if (typeof error === 'object' &&
                typeof error.message === 'string') {
                errorMessage = error.message;
            }
        }
        if (errorMessage) {
            if (recurringErrors.has(errorMessage)) {
                const { timeoutId } = recurringErrors.get(errorMessage);
                clearTimeout(timeoutId);
            }
            else {
                reportToDataDog(error, context);
            }
            recurringErrors.set(errorMessage, {
                timeoutId: setTimeout(() => recurringErrors.delete(errorMessage), 1000)
            });
        }
        else {
            reportToDataDog(error, context);
        }
    };
}
export function isThirdPartyFetchError(error) {
    const errorMessage = typeof error === 'string' ? error : error?.message || '';
    const errorStack = typeof error === 'string' ? '' : error?.stack || '';
    // Check if it's a fetch error from third-party domains
    const thirdPartyDomains = [
        'px.ads.linkedin.com',
        'siteperformancetest.net',
        'hsm.honeybook.com',
        'ads.linkedin.com'
    ];
    const combinedText = `${errorMessage} ${errorStack}`.toLowerCase();
    // Check for "Fetch error" pattern with third-party domains
    if (errorMessage.includes('Fetch error')) {
        return thirdPartyDomains.some(domain => combinedText.includes(domain.toLowerCase()));
    }
    return false;
}
function reportToDataDog(error, context) {
    // Skip logging third-party fetch errors
    if (isThirdPartyFetchError(error)) {
        return;
    }
    if (!isProduction()) {
        const message = typeof error === 'string' ? error : error.message;
        logToConsole('error', message, context);
    }
    const datadog = window.DD_RUM;
    if (!datadog || !datadog.addError) {
        return;
    }
    datadog.addError(error, context);
}
const debounceReportToDataDog = debounceReportRecurringDataDogErrors();
export const ddRumLogger = {
    debug: console.debug,
    info: console.info,
    warn: console.warn,
    error: debounceReportToDataDog
};
const isDevelopment = () => process.env.NODE_ENV === 'development';
const isTesting = () => process.env.NODE_ENV === 'test';
export function trackError({ error, message, context = {}, enrichContext }) {
    const enrichedContext = enrichContext ? enrichContext(context) : context;
    let errorToPass;
    let contextToPass;
    if (error instanceof Error) {
        if (error instanceof AxiosError) {
            const { url, method, ...responseFields } = toAxiosLogFields(error);
            Object.assign(enrichedContext, { url, method });
            // Deliberately excludes responseHeaders, fullResponseData,
            // errorBacktrace, and requestData -- those can carry request/response
            // bodies (contact details, payment info, etc.) or server stack
            // traces, and cleanAxiosError() below only scrubs the AxiosError
            // object itself, not this context copy.
            if (error.response) {
                Object.assign(enrichedContext, responseFields);
            }
            if (error.code) {
                Object.assign(enrichedContext, {
                    errorCode: error.code,
                    errorMessage: error.message
                });
            }
            cleanAxiosError(error);
        }
        errorToPass = message ? new Error(message, { cause: error }) : error;
        contextToPass = enrichedContext;
    }
    else if (isAxiosResponseAsError(error)) {
        const errorMessage = error.data?.error_message;
        const method = error.config?.method?.toUpperCase() || 'Unknown';
        const url = error.config?.url || 'Unknown URL';
        const status = error.status || 0;
        // Never create an Error with an empty message ("Empty reason" in Datadog)
        const finalMessage = errorMessage &&
            typeof errorMessage === 'string' &&
            errorMessage.trim().length > 0
            ? errorMessage
            : `API request failed: ${method} ${url}${status ? ` (${status})` : ''}`;
        errorToPass = new Error(message || finalMessage);
        // Deliberately excludes responseHeaders, fullResponseData, errorBacktrace,
        // and requestData -- unlike the AxiosError branch above, this branch never
        // calls cleanAxiosError(), so nothing else scrubs those from `error` either.
        contextToPass = {
            ...enrichedContext,
            ...toAxiosLogFields(error),
            originalErrorMessage: errorMessage,
            timestamp: new Date().toISOString()
        };
    }
    else if (message) {
        errorToPass = new Error(message);
        contextToPass = { ...enrichedContext, cause: stringify(error) };
    }
    else {
        errorToPass = error;
        contextToPass = enrichedContext;
    }
    ddRumLogger.error(errorToPass, contextToPass);
    if (isDevelopment() || isTesting()) {
        // eslint-disable-next-line no-restricted-syntax
        console.error(message, { error, errorToPass, contextToPass });
    }
}
const MAX_LOGGED_CAUSE_DEPTH = 5;
/**
 * What `ILogger.error` logs for `error`: its name, message and stack and those
 * of each error down its `cause` chain, plus the safe-to-log axios fields found
 * anywhere in that chain. Reads only; the error is left as it is.
 */
export function getErrorLogContext(error) {
    if (!(error instanceof Error)) {
        return {};
    }
    return { error: describeError(error, 0), ...getAxiosLogContext(error) };
}
function describeError(error, depth) {
    if (isAxiosResponseAsError(error)) {
        return 'AxiosResponse';
    }
    if (!(error instanceof Error)) {
        return stringify(error);
    }
    return {
        kind: error.name,
        message: error.message,
        stack: error.stack,
        ...(error.cause !== undefined &&
            depth < MAX_LOGGED_CAUSE_DEPTH && {
            cause: describeError(error.cause, depth + 1)
        })
    };
}
function getAxiosLogContext(error, depth = 0) {
    if (error instanceof AxiosError) {
        return { ...toAxiosLogFields(error), errorCode: error.code };
    }
    if (isAxiosResponseAsError(error)) {
        return toAxiosLogFields(error);
    }
    if (error instanceof Error &&
        error.cause !== undefined &&
        depth < MAX_LOGGED_CAUSE_DEPTH) {
        return getAxiosLogContext(error.cause, depth + 1);
    }
    return {};
}
function stringify(obj) {
    try {
        return JSON.stringify(obj);
    }
    catch (e) {
        return `unable to stringify: '${e.message}'`;
    }
}
//# sourceMappingURL=error.utils.js.map